import React, { useState, useEffect } from 'react';
import { driverAPI, rideAPI } from '../../lib/api';
import { formatCurrency, getStatusColor, getStatusText } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LiveMap from '../../components/LiveMap';
import RideChat from '../../components/RideChat';
import { MapPin, Navigation, Play, CheckCircle, Loader2, Map } from 'lucide-react';
import { toast } from 'sonner';

const ActiveRides = () => {
  const [loading, setLoading] = useState(true);
  const [rides, setRides] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedRideId, setExpandedRideId] = useState(null);
  const [currentNavStep, setCurrentNavStep] = useState(null);

  useEffect(() => {
    loadRides();
    const interval = setInterval(loadRides, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadRides = async () => {
    try {
      const response = await driverAPI.getActiveRides();
      setRides(response.data);
    } catch (error) {
      console.error('Error loading rides:', error);
    } finally {
      setLoading(false);
    }
  };

  const startRide = async (rideId) => {
    setActionLoading(`start-${rideId}`);
    try {
      await rideAPI.startRide(rideId);
      toast.success('Ride started!');
      loadRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start ride');
    } finally {
      setActionLoading(null);
    }
  };

  const completeRide = async (rideId) => {
    setActionLoading(`complete-${rideId}`);
    try {
      await rideAPI.completeRide(rideId);
      toast.success('Ride completed! Great job!');
      loadRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete ride');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="active-rides-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Active Rides
        </h1>

        {rides.length === 0 ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Navigation className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                No active rides
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Accept a ride to get started!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {rides.map((ride) => (
              <Card 
                key={ride.id} 
                className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                data-testid={`active-ride-${ride.id}`}
              >
                <CardContent className="p-6">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <Badge className={`${getStatusColor(ride.status)} font-semibold uppercase text-xs`}>
                      {getStatusText(ride.status)}
                    </Badge>
                    <span className="font-display font-bold text-xl text-green-600">
                      {formatCurrency(ride.driver_payout)}
                    </span>
                  </div>

                  {/* Locations */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Pickup</p>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {ride.pickup_location?.address || `${ride.pickup_location?.lat?.toFixed(4)}, ${ride.pickup_location?.lng?.toFixed(4)}`}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Dropoff</p>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {ride.dropoff_location?.address || `${ride.dropoff_location?.lat?.toFixed(4)}, ${ride.dropoff_location?.lng?.toFixed(4)}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ride Info */}
                  <div className="flex items-center gap-6 mb-6 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Distance</p>
                      <p className="font-display font-bold text-slate-900 dark:text-white">
                        {ride.distance_miles?.toFixed(1)} mi
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Est. Time</p>
                      <p className="font-display font-bold text-slate-900 dark:text-white">
                        {ride.duration_minutes} min
                      </p>
                    </div>
                    <div className="flex-1 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedRideId(expandedRideId === ride.id ? null : ride.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Map className="w-4 h-4 mr-1" />
                        {expandedRideId === ride.id ? 'Hide Map' : 'Show Map'}
                      </Button>
                    </div>
                  </div>

                  {/* In-app Navigation Map */}
                  {expandedRideId === ride.id && (
                    <div className="mb-6 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                      <LiveMap
                        pickup={ride.pickup_location}
                        dropoff={ride.dropoff_location}
                        showRoute={true}
                        showNavigation={ride.status === 'in_progress'}
                        onNavigationStep={setCurrentNavStep}
                        height="300px"
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    {ride.status === 'accepted' && (
                      <Button
                        onClick={() => startRide(ride.id)}
                        disabled={actionLoading === `start-${ride.id}`}
                        className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 font-bold uppercase tracking-wider"
                        data-testid={`start-ride-btn-${ride.id}`}
                      >
                        {actionLoading === `start-${ride.id}` ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Start Ride
                          </>
                        )}
                      </Button>
                    )}

                    {ride.status === 'in_progress' && (
                      <Button
                        onClick={() => completeRide(ride.id)}
                        disabled={actionLoading === `complete-${ride.id}`}
                        className="flex-1 rounded-full bg-green-600 hover:bg-green-700 font-bold uppercase tracking-wider"
                        data-testid={`complete-ride-btn-${ride.id}`}
                      >
                        {actionLoading === `complete-${ride.id}` ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Complete Ride
                          </>
                        )}
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      className="rounded-full font-semibold"
                      onClick={() => {
                        const pickup = ride.pickup_location;
                        const dropoff = ride.dropoff_location;
                        window.open(
                          `https://www.google.com/maps/dir/${pickup.lat},${pickup.lng}/${dropoff.lat},${dropoff.lng}`,
                          '_blank'
                        );
                      }}
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Navigate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Chat Component - Show for active ride */}
        {rides.length > 0 && ['accepted', 'in_progress'].includes(rides[0]?.status) && (
          <RideChat 
            rideId={rides[0].id} 
            otherPartyName={rides[0].rider?.full_name || 'Rider'} 
          />
        )}
      </div>
    </div>
  );
};

export default ActiveRides;
